import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CELL, MonthlyGrid } from '@/components/staffAttendance/MonthlyGrid';
import { CollarSelect, SectionSelect, WorkerTypeSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { matchStaff, SearchNote } from '@/components/staffAttendance/StaffSearch';
import { staffAttApi, errMsg, todayIST } from '@/services/staffAttendanceApi';
import { downloadCsv } from '@/services/transportApi';

const STATS = [['staff', 'Staff'], ['present', 'Present days'], ['absent', 'Absent'], ['late', 'Late'], ['early', 'Left early'], ['missed', 'Missed out'], ['off_site', 'Off-site'], ['hours', 'Hours']];

const SectionSummary = ({ sections }) => (
  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="monthly-summary">
    {sections.map((s) => (
      <div key={s.section} className="rounded-xl bg-white shadow-md p-4" data-testid={`monthly-summary-${s.section}`}>
        <div className="font-semibold mb-2">{s.section}</div>
        <div className="grid grid-cols-4 gap-2 text-center">
          {STATS.map(([k, l]) => <div key={k}><div className="text-lg font-bold font-mono" data-testid={`monthly-summary-${s.section}-${k}`}>{s[k]}</div><div className="text-[10px] uppercase text-gray-500">{l}</div></div>)}
        </div>
      </div>
    ))}
  </div>
);

const Legend = () => (
  <div className="flex flex-wrap gap-2 text-xs text-gray-600" data-testid="monthly-legend">
    {Object.entries(CELL).filter(([, c]) => c.code).map(([k, c]) => <span key={k} className="inline-flex items-center gap-1"><span className={`rounded px-1.5 font-bold ${c.cls}`}>{c.code}</span>{c.label}</span>)}
    <span className="inline-flex items-center gap-1"><span className="text-red-600 font-bold">*</span>Off-site check-in</span>
    <span className="inline-flex items-center gap-1"><span className="text-indigo-600 font-bold">↗</span>Worked in another section (deployed)</span>
  </div>
);

export const MonthlyView = ({ sections, search = '' }) => {
  const [month, setMonth] = useState(todayIST().slice(0, 7));
  const [section, setSection] = useState('');
  const [wt, setWt] = useState('');
  const [collar, setCollar] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const params = { month, section: section || undefined, worker_type: wt || undefined, collar: collar || undefined };
  useEffect(() => {
    let alive = true;
    setData(null);
    staffAttApi.monthly({ month, section: section || undefined, worker_type: wt || undefined, collar: collar || undefined })
      .then((d) => alive && setData(d)).catch((e) => { if (alive) { setData({ staff: [], sections: [] }); toast.error(errMsg(e, 'Failed to load monthly attendance')); } });
    return () => { alive = false; };
  }, [month, section, wt, collar]);
  const shownStaff = (data?.staff || []).filter((r) => matchStaff(search, r.staff_name, r.staff_id));
  const csv = async () => {
    setBusy(true);
    const q = new URLSearchParams(Object.entries({ ...params, format: 'csv' }).filter(([, v]) => v)).toString();
    try { await downloadCsv(`/staff-attendance/monthly?${q}`, `staff_attendance_monthly_${month}.csv`); } catch { toast.error('Download failed'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="monthly-view">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-[170px] bg-white" data-testid="monthly-month-input" />
        <SectionSelect sections={sections} value={section} onChange={setSection} testId="monthly-section-select" />
        <WorkerTypeSelect value={wt} onChange={setWt} testId="monthly-worker-select" />
        <CollarSelect value={collar} onChange={setCollar} testId="monthly-collar-select" />
        <Button onClick={csv} disabled={busy} variant="outline" className="ml-auto border-amber-400 text-amber-700" data-testid="monthly-csv-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download CSV
        </Button>
      </div>
      {data?.collar_split && <CollarTiles split={data.collar_split} value={collar} onPick={setCollar} metric="Present days" sub={(s) => `${s.staff} staff`} testId="monthly-collar-tiles" />}
      {!data && <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" />}
      {data && data.staff.length === 0 && <p className="text-center text-gray-400 py-10" data-testid="monthly-empty">No staff for this filter.</p>}
      {data && data.staff.length > 0 && (
        <>
          <SectionSummary sections={data.sections} />
          <Legend />
          <SearchNote q={search} shown={shownStaff.length} total={data.staff.length} testId="monthly-search-note" />
          {shownStaff.length > 0 && <MonthlyGrid data={{ ...data, staff: shownStaff }} />}
        </>
      )}
    </div>
  );
};

export default MonthlyView;
